import { VehicleType } from "@daytrip/legacy-enums";
import { PriceCalculator } from "@daytrip/legacy-models";
import type { Location } from "@daytrip/legacy-models";
import type { OrderContentLocation } from "@daytrip/legacy-models";
import type { OrderLocationVehicleTypePriceFee } from "@daytrip/legacy-models";
import type { SimpleCountry, PricingArea } from "@daytrip/legacy-models";
import { getDefaultFeeCoefficient, getVehicleTypeFeeCoefficient } from "@daytrip/legacy-utils";
import autobind from "autobind-decorator";
import { action, computed, observable } from "mobx";

import { ModelOperator } from "./ModelOperator";
import type { ModelOperatorOptions } from "./ModelOperatorOptions";

export type OrderContentLocationOperatorOptions = ModelOperatorOptions<
    OrderContentLocation,
    null,
    OrderContentLocationOperatorData
>;

interface OrderContentLocationOperatorData {
    countries: Array<SimpleCountry>;
    location: Location;
}

interface OrderContentLocationOperatorDataFetched {
    countries: Array<SimpleCountry>;
    location: Location;
}

@autobind
export class ContentLocationOperator extends ModelOperator<
    OrderContentLocation,
    OrderContentLocationOperatorOptions,
    null,
    OrderContentLocationOperatorData,
    OrderContentLocationOperatorDataFetched
> {
    @observable
    public opened: boolean = false;

    @action
    public close() {
        this.opened = false;
    }

    @action
    public open() {
        this.opened = true;
    }

    @computed
    public get country(): SimpleCountry | undefined {
        return this.data.countries?.find((c) => c._id === this.data.location.countryId);
    }

    @action
    public orderDown() {
        this.edit((cl) => {
            cl.order -= 1;
        });
    }

    @action
    public orderUp() {
        this.edit((cl) => {
            cl.order += 1;
        });
    }

    @action
    public orderSet(newOrder: number) {
        this.edit((cl) => {
            cl.order = newOrder;
        });
    }

    @action
    public setDuration(duration: number, pricingCountryId: string) {
        this.edit(async (cl) => {
            const routePricingCountry = this.data.countries.find((country) => country._id === pricingCountryId);

            if (!routePricingCountry) {
                return;
            }

            cl.duration = duration;

            for (const vehiclePrice of cl.vehicleTypesPricesFees) {
                const { driverWaitingPrice, driverWaitingFee } =
                    PriceCalculator.calculateDriverLocationPriceFeeForVehicle(
                        routePricingCountry as unknown as PricingArea,
                        duration,
                        vehiclePrice.vehicleType,
                    );
                vehiclePrice.driverWaitingPrice = driverWaitingPrice;
                vehiclePrice.driverWaitingFee = driverWaitingFee;
            }
        });
    }

    @action
    public setVehiclePrice(
        vehicleType: VehicleType,
        vehiclePrice: number,
        waitingPrice: number,
        pricingCountryId: string,
    ) {
        let vehicleTypePriceFeeSetPriceIndex = this.m.vehicleTypesPricesFees.findIndex(
            (vtpf) => vtpf.vehicleType === vehicleType,
        );

        if (vehicleTypePriceFeeSetPriceIndex === -1) {
            this.m.vehicleTypesPricesFees.push({
                vehicleType,
                price: 0,
                fee: 0,
                isManuallyModified: false,
                driverWaitingPrice: 0,
                driverWaitingFee: 0,
            } as OrderLocationVehicleTypePriceFee);

            vehicleTypePriceFeeSetPriceIndex = this.m.vehicleTypesPricesFees.findIndex(
                (vtpf) => vtpf.vehicleType === vehicleType,
            );
        }

        const pricingCountry = this.data.countries.find((country) => country._id === pricingCountryId) as SimpleCountry;
        const feeCoefficient = getVehicleTypeFeeCoefficient(pricingCountry, vehicleType);
        const price = Math.round(vehiclePrice * feeCoefficient);
        const fee = Math.round(vehiclePrice - price);
        const driverWaitingPrice = Math.round(waitingPrice * feeCoefficient);
        const driverWaitingFee = Math.round(waitingPrice - driverWaitingPrice);
        this.edit((cl) => {
            cl.vehicleTypesPricesFees[vehicleTypePriceFeeSetPriceIndex].price = price;
            cl.vehicleTypesPricesFees[vehicleTypePriceFeeSetPriceIndex].fee = fee;
            cl.vehicleTypesPricesFees[vehicleTypePriceFeeSetPriceIndex].isManuallyModified = true;
            cl.vehicleTypesPricesFees[vehicleTypePriceFeeSetPriceIndex].driverWaitingPrice = driverWaitingPrice;
            cl.vehicleTypesPricesFees[vehicleTypePriceFeeSetPriceIndex].driverWaitingFee = driverWaitingFee;
        });
    }

    @action
    public setAdditionalPrice(additionalPrice: number, pricingCountryId: string) {
        this.edit((cl) => {
            cl.additionalPrice = additionalPrice;
            const feeCoefficient = getDefaultFeeCoefficient(
                this.data.countries.find((country) => country._id === pricingCountryId) as SimpleCountry,
            );
            cl.additionalFee = PriceCalculator.calculateFeeFromPrice(cl.additionalPrice, feeCoefficient);
        });
    }

    @action
    public setAdditionalFee(additionalFee: number) {
        this.edit((cl) => {
            cl.additionalFee = additionalFee;
        });
    }

    @action
    public setExtraPrice(priceType: string, extraPrice: number, pricingCountryId: string) {
        const feeCoefficient = getDefaultFeeCoefficient(
            this.data.countries.find((country) => country._id === pricingCountryId) as SimpleCountry,
        );
        const price = Math.round(extraPrice * feeCoefficient);
        const fee = Math.round(extraPrice - price);

        this.edit((cl) => {
            cl[`${priceType}Price`] = price;
            cl[`${priceType}Fee`] = fee;
        });
    }
}
